/**
 *
 * Teams Form
 *
 */

import React, { FC, useEffect, useReducer, useState } from 'react';

import { Clients } from '@ariksa/notification';
import { Alert, AlertIcon, Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { Form, FormAction } from 'components/index';
import { useIntegrations } from 'containers/Setup/Integrations/hooks/useIntegrations';
import { selectIntegrations } from 'containers/Setup/Integrations/selectors';
import { actions } from 'containers/Setup/Integrations/slice';

import { formStyles } from '../../../../styles';

import { TeamsHelp } from './TeamsHelp';

interface Props {
  handleSubmit: (data: any) => void;
  actionType?: FormAction;
}

export const TeamsForm: FC<Props> = props => {
  const { handleSubmit, actionType = 'Add' } = props;
  const { client, validateClient, activeClient } = useSelector(
    selectIntegrations,
  );
  const { onCancel } = useIntegrations();
  const { data: currentRecord } = activeClient;

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const toggleIsHelpOpen = () => setIsHelpOpen(!isHelpOpen);
  const dispatch = useDispatch();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      name: '',
      webhook_url: '',
      channel_name: '',
      validateChannel: false,
      showValidatedMessage: false,
      isEdit: false,
    },
  );

  //set isEdit true if opened in edit
  useEffect(() => {
    updateState({
      isEdit: actionType === 'Update',
      validateChannel: actionType === 'Update',
    });
  }, [actionType]);

  const schemaForm = yup.object().shape({
    name: yup
      .string()
      .matches(
        /^[a-zA-Z0-9-_ ]*$/i,
        'Only alphanumeric, hyphen, underscore and space is allowed!',
      ),
  });

  useEffect(() => {
    updateState({
      name: currentRecord?.name,
      channel_name: currentRecord?.teams?.channel_name,
    });
  }, [currentRecord]);

  const validateChannel = data => {
    dispatch(
      actions.validateClient({
        q: {
          clientConfig: {
            name: data.name,
            status: true,
            client_name: Clients.Teams,
            teams: {
              channel_name: data?.channel_name,
              webhook_url: data?.webhook_url,
            },
          },
        },
        onSuccess: res =>
          updateState({
            validateChannel: true,
            showValidatedMessage: true,
          }),
        onError: res =>
          updateState({ validateChannel: false, showValidatedMessage: true }),
      }),
    );
  };

  return (
    <Form
      formValidation={schemaForm}
      isLoading={activeClient.isLoading}
      schema={{
        name: {
          type: 'text',
          label: 'Name',
          isRequired: true,
          defaultValue: currentRecord?.name,
          isDisabled: client.isLoading,
          onChange: value => updateState({ name: value }),
          tooltip: 'Only alphanumeric, hyphen, underscore and space is allowed',
          autoFocus: true,
        },
        ...(state.isEdit
          ? {}
          : {
              webhook_url: {
                type: 'text',
                label: 'Webhook URL',
                isRequired: true,
                isDisabled: state.isEdit || client.isLoading,
                value: state.webhook_url,
                onChange: value =>
                  updateState({
                    webhook_url: value,
                    validateChannel: false,
                    showValidatedMessage: false,
                  }),
              },
            }),
        channel_name: {
          type: 'text',
          label: 'Channel Name',
          isRequired: true,
          value: state.channel_name,
          onChange: value =>
            updateState({
              channel_name: value,
              validateChannel: false,
              showValidatedMessage: false,
            }),
          isDisabled: client.isLoading,
        },
        ...(state.showValidatedMessage
          ? {
              validatedMessage: {
                type: 'custom',
                component: () => {
                  const err = validateClient.error?.title;
                  const success = !err;
                  return (
                    <Box w="full">
                      <Alert
                        status={success ? 'success' : 'error'}
                        borderRadius={6}
                        title={success ? `` : validateClient.error?.title}
                      >
                        <AlertIcon />
                        {success
                          ? `All fields are valid. Please click on "Add" to proceed.`
                          : validateClient.error?.description}
                      </Alert>
                    </Box>
                  );
                },
              },
            }
          : {}),
      }}
      buttonOptions={{
        submit: {
          name: !state.validateChannel
            ? 'VALIDATE'
            : state.isEdit
            ? 'UPDATE'
            : 'ADD',
          isLoading: client.isLoading,
          isDisabled:
            client.isLoading ||
            !state.channel_name ||
            !state.name ||
            (!state.webhook_url && !state.isEdit),
        },
        reset: {
          isVisible: true,
          isDisabled: client.isLoading,
          onClick: onCancel,
        },
      }}
      showHelp
      styles={formStyles}
      handleSubmit={state.validateChannel ? handleSubmit : validateChannel}
    />
  );
};
